/**
 * Modos de transporte suportados e resolução SEM fallback silencioso.
 *
 * Regras vinculativas:
 * - existem exatamente cinco modos: a pé, carro, trotinete, bicicleta e
 *   transportes públicos;
 * - se o fornecedor não suportar o modo pedido naquele percurso, a perna fica
 *   com `status: "unavailable"` — nunca é apresentada como outro modo;
 * - módulo puro.
 */

export const TRAVEL_MODES = ["walk", "car", "scooter", "bike", "transit"] as const;
export type TravelModeId = (typeof TRAVEL_MODES)[number];

export type LegStatus = "ok" | "unavailable" | "pending";

export function isTravelMode(value: string): value is TravelModeId {
  return (TRAVEL_MODES as readonly string[]).includes(value);
}

/**
 * Resolve o modo real de uma perna. Sem modo devolvido pelo fornecedor,
 * assume-se o pedido; com modo diferente do pedido, a perna é `unavailable`.
 */
export function resolveLegMode(
  requestedMode: TravelModeId,
  providerMode: string | null | undefined,
  hasMetrics: boolean,
): { resolvedMode: TravelModeId | null; status: LegStatus } {
  if (providerMode && isTravelMode(providerMode) && providerMode !== requestedMode) {
    return { resolvedMode: providerMode, status: "unavailable" };
  }
  if (!hasMetrics) return { resolvedMode: null, status: "pending" };
  return { resolvedMode: requestedMode, status: "ok" };
}

export function modeUnavailableNotice(mode: TravelModeId, lang = "pt"): string {
  const names: Record<TravelModeId, Record<string, string>> = {
    walk: { pt: "a pé", en: "walking" },
    car: { pt: "de carro", en: "by car" },
    scooter: { pt: "de trotinete", en: "by scooter" },
    bike: { pt: "de bicicleta", en: "by bike" },
    transit: { pt: "de transportes públicos", en: "by public transport" },
  };
  const isPt = (lang || "pt").slice(0, 2).toLowerCase() === "pt";
  const name = names[mode][isPt ? "pt" : "en"];
  return isPt
    ? `Não foi possível calcular este percurso ${name}. Escolhe outro modo de transporte.`
    : `This route could not be calculated ${name}. Please choose another travel mode.`;
}
